/**
 * VoiceAgent - Structured step-by-step voice assistant
 * Continuous listening with pause, structured deviz flow, learns from existing data
 */
import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mic, MicOff, X, Send, Volume2, Loader2, Sparkles, MessageSquare, Pause, Play, Check, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: "beneficiar", label: "Beneficiar" },
  { id: "adresa", label: "Adresa" },
  { id: "executant", label: "Executant" },
  { id: "obiectiv", label: "Obiectiv" },
  { id: "template", label: "Sablon activitati" },
  { id: "extra", label: "Activitati extra" },
];

const STEP_PROMPTS = {
  beneficiar: "Spuneti numele beneficiarului lucrarii.",
  adresa: "Care este adresa lucrarii?",
  executant: "Cine este executantul lucrarii?",
  obiectiv: "Care este obiectivul sau descrierea lucrarii?",
  template: "Ce tip de lucrare doriti? Sabloane: hidroizolatii, sapa, tencuiala, gresie si faianta, termoizolatie, instalatii electrice, sanitare, parchet.",
  extra: "Doriti sa adaugati activitati suplimentare? Spuneti da sau nu.",
};

export default function VoiceAgent({ companyId }) {
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [paused, setPaused] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [textInput, setTextInput] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1); // -1 = not started
  const [devizData, setDevizData] = useState({});
  const [templateData, setTemplateData] = useState(null);
  const [mode, setMode] = useState("free"); // free | structured
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const chatEndRef = useRef(null);
  const audioRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const isListeningRef = useRef(false);
  const isPausedRef = useRef(false);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => { return () => stopListeningInternal(); }, []);

  const stopListeningInternal = () => {
    isListeningRef.current = false;
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch (e) { /* */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const speakText = async (text) => {
    try {
      const wasListening = isListeningRef.current && !isPausedRef.current;
      if (wasListening) {
        isPausedRef.current = true;
        if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
      }
      setSpeaking(true);
      const ttsRes = await axios.post(`${API}/voice/tts`, { text }, { responseType: "blob" });
      const audioUrl = URL.createObjectURL(ttsRes.data);
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => {
          setSpeaking(false);
          if (wasListening && isListeningRef.current) {
            isPausedRef.current = false;
            setPaused(false);
            startRecordingCycle();
          }
        };
        await audioRef.current.play();
      }
    } catch (e) {
      console.error("TTS:", e);
      setSpeaking(false);
    }
  };

  const startContinuousListening = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;
      setListening(true);
      setPaused(false);
      isListeningRef.current = true;
      isPausedRef.current = false;
      startRecordingCycle();
    } catch (error) {
      toast.error("Nu am acces la microfon.");
    }
  }, []);

  const startRecordingCycle = () => {
    if (!streamRef.current || !isListeningRef.current) return;
    const mediaRecorder = new MediaRecorder(streamRef.current, { mimeType: "audio/webm" });
    mediaRecorderRef.current = mediaRecorder;
    chunksRef.current = [];
    let hasSound = false;

    mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mediaRecorder.onstop = async () => {
      if (!isListeningRef.current) return;
      if (hasSound && chunksRef.current.length > 0) {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 1000) await processAudio(blob);
      }
      if (isListeningRef.current && !isPausedRef.current) setTimeout(() => startRecordingCycle(), 300);
    };
    mediaRecorder.start(250);

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    let silenceStart = null, speakingDetected = false;
    const checkAudio = () => {
      if (!isListeningRef.current || isPausedRef.current) return;
      analyserRef.current.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      if (avg > 15) { silenceStart = null; if (!speakingDetected) { speakingDetected = true; hasSound = true; } }
      else if (speakingDetected) {
        if (!silenceStart) silenceStart = Date.now();
        else if (Date.now() - silenceStart > 1500) { if (mediaRecorder.state === "recording") { mediaRecorder.stop(); return; } }
      }
      animFrameRef.current = requestAnimationFrame(checkAudio);
    };
    animFrameRef.current = requestAnimationFrame(checkAudio);
    setTimeout(() => { if (mediaRecorder.state === "recording") mediaRecorder.stop(); }, 30000);
  };

  const togglePause = () => {
    if (paused) { setPaused(false); isPausedRef.current = false; startRecordingCycle(); }
    else { setPaused(true); isPausedRef.current = true; if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop(); }
  };

  const stopListening = () => { stopListeningInternal(); setListening(false); setPaused(false); };

  const processAudio = async (blob) => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");
      const res = await axios.post(`${API}/voice/transcribe`, formData);
      const text = res.data.text;
      if (!text?.trim() || text.trim().length < 2) { setProcessing(false); return; }
      await handleUserInput(text);
    } catch (e) { console.error("Process:", e); setProcessing(false); }
  };

  const handleUserInput = async (text) => {
    setProcessing(true);
    setMessages(prev => [...prev, { role: "user", text }]);

    if (mode === "structured" && currentStep >= 0) {
      await processStructuredStep(text);
    } else {
      await processFreeChat(text);
    }
    setProcessing(false);
  };

  const processStructuredStep = async (text) => {
    const step = STEPS[currentStep];
    try {
      const res = await axios.post(`${API}/voice/structured-step`, {
        step_id: step.id,
        user_text: text,
        company_id: companyId,
        deviz_data: devizData,
      });

      const { value, confirmed, message, template_data: tplData } = res.data;

      if (confirmed && value) {
        setDevizData(prev => ({ ...prev, [step.id]: value }));
        if (tplData) setTemplateData(tplData);

        // Move to next step
        const nextIdx = currentStep + 1;
        if (step.id === "extra" && value.toLowerCase() === "nu") {
          // Done! Create deviz
          setMessages(prev => [...prev, { role: "assistant", text: message + " Creez devizul acum..." }]);
          await speakText(message + " Creez devizul acum.");
          await createDevizFromVoice();
          return;
        }
        if (nextIdx < STEPS.length) {
          setCurrentStep(nextIdx);
          const nextPrompt = STEP_PROMPTS[STEPS[nextIdx].id];
          const fullMsg = message + " " + nextPrompt;
          setMessages(prev => [...prev, { role: "assistant", text: fullMsg, template_data: tplData }]);
          await speakText(fullMsg);
        }
      } else {
        setMessages(prev => [...prev, { role: "assistant", text: message }]);
        await speakText(message);
      }
    } catch (e) {
      console.error("Structured step error:", e);
      setMessages(prev => [...prev, { role: "assistant", text: "Eroare. Repetati, va rog." }]);
    }
  };

  const processFreeChat = async (text) => {
    try {
      const res = await axios.post(`${API}/voice/chat`, { message: text, session_id: sessionId, company_id: companyId });
      setSessionId(res.data.session_id);
      let aiText = res.data.response;
      if (res.data.template_data) {
        aiText += `\n\n${res.data.template_data.name} — ${res.data.template_data.activities.length} activitati.`;
        setTemplateData(res.data.template_data);
      }
      setMessages(prev => [...prev, { role: "assistant", text: aiText, template_data: res.data.template_data }]);
      await speakText(res.data.response);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", text: "Eroare. Incercati din nou." }]);
    }
  };

  const createDevizFromVoice = async () => {
    try {
      const activities = templateData?.activities || [];
      const res = await axios.post(`${API}/devizpro/create-deviz?company_id=${companyId}`, {
        activities, beneficiar: devizData.beneficiar || "", obiectiv: devizData.obiectiv || "",
      });
      const msg = `Devizul ${res.data.deviz?.numarDeviz} a fost creat cu succes!`;
      setMessages(prev => [...prev, { role: "assistant", text: msg }]);
      await speakText(msg);
      setCurrentStep(-1);
      setMode("free");
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", text: "Eroare la crearea devizului." }]);
    }
  };

  const startStructuredFlow = () => {
    setMode("structured");
    setCurrentStep(0);
    setDevizData({});
    setTemplateData(null);
    const prompt = "Sa incepem! " + STEP_PROMPTS["beneficiar"];
    setMessages(prev => [...prev, { role: "assistant", text: prompt }]);
    speakText(prompt);
  };

  const handleTextSend = () => {
    if (!textInput.trim() || processing) return;
    const msg = textInput;
    setTextInput("");
    handleUserInput(msg);
  };

  const resetAll = () => {
    setMessages([]); setSessionId(null); setCurrentStep(-1); setMode("free");
    setDevizData({}); setTemplateData(null);
  };

  if (!open) {
    return (
      <button
        onClick={() => {
          setOpen(true);
          if (messages.length === 0) {
            setMessages([{
              role: "assistant",
              text: "Buna! Sunt agentul vocal e-TESA.\n\nApasa 'Deviz Vocal' pentru a crea un deviz pas cu pas, sau vorbeste liber."
            }]);
          }
        }}
        className="fixed bottom-20 left-5 z-[60] w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-xl hover:shadow-2xl hover:scale-110 transition-all flex items-center justify-center"
        title="Agent Vocal e-TESA"
        data-testid="voice-agent-btn"
      >
        <Mic className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-20 left-5 z-[60] w-[360px] max-h-[540px] flex flex-col bg-white rounded-2xl shadow-2xl border border-amber-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-300" data-testid="voice-agent-panel">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold text-sm">Agent Vocal</span>
          <Badge className="bg-white/20 text-[9px] border-0 font-bold">e-TESA</Badge>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={resetAll} className="p-1.5 rounded-lg hover:bg-white/20" title="Conversatie noua"><MessageSquare className="w-4 h-4" /></button>
          <button onClick={() => { setOpen(false); stopListening(); }} className="p-1.5 rounded-lg hover:bg-white/20"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Step progress bar (structured mode) */}
      {mode === "structured" && currentStep >= 0 && (
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-100">
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center gap-0.5 flex-1">
                <div className={`h-1.5 flex-1 rounded-full transition-colors ${i < currentStep ? "bg-green-400" : i === currentStep ? "bg-amber-400" : "bg-slate-200"}`} />
              </div>
            ))}
          </div>
          <p className="text-[10px] text-amber-700 mt-1 font-medium">
            Pas {currentStep + 1}/{STEPS.length}: {STEPS[currentStep]?.label}
          </p>
        </div>
      )}

      {/* Status bar */}
      {listening && (
        <div className={`px-3 py-1 text-[10px] font-medium flex items-center justify-center gap-2 ${
          paused ? "bg-yellow-50 text-yellow-700" : speaking ? "bg-blue-50 text-blue-600" : "bg-green-50 text-green-700"
        }`}>
          {paused ? <><Pause className="w-3 h-3" /> Pauza</> :
           speaking ? <><Volume2 className="w-3 h-3 animate-pulse" /> Vorbesc...</> :
           processing ? <><Loader2 className="w-3 h-3 animate-spin" /> Procesez...</> :
           <><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Ascult...</>}
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 px-3 py-2 min-h-0" style={{ maxHeight: "280px" }}>
        <div className="space-y-2">
          {/* Quick action button if not in structured mode */}
          {mode === "free" && currentStep === -1 && messages.length <= 1 && (
            <button
              onClick={startStructuredFlow}
              className="w-full p-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 hover:border-amber-300 transition-colors text-left"
              data-testid="voice-start-structured-btn"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-400 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-800">Deviz Vocal Ghidat</p>
                  <p className="text-[10px] text-amber-600">Pas cu pas: beneficiar, adresa, sablon activitati</p>
                </div>
                <ArrowRight className="w-4 h-4 text-amber-400 shrink-0" />
              </div>
            </button>
          )}

          {messages.map((msg, i) => (
            <div key={`vmsg-${i}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs ${
                msg.role === "user" ? "bg-amber-500 text-white rounded-br-sm" : "bg-slate-100 text-slate-700 rounded-bl-sm"
              }`}>
                <div className="whitespace-pre-wrap">{msg.text}</div>
                {msg.template_data && (
                  <div className="mt-2 p-2 bg-white/50 rounded-lg border border-amber-200">
                    <p className="font-semibold text-amber-700 text-[10px]">{msg.template_data.name}:</p>
                    {msg.template_data.activities.slice(0, 5).map((a, j) => (
                      <p key={`tpl-${j}`} className="text-[10px] text-slate-500">{a.code} — {a.name}</p>
                    ))}
                    {msg.template_data.activities.length > 5 && (
                      <p className="text-[10px] text-amber-500">+{msg.template_data.activities.length - 5} activitati</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {processing && !speaking && (
            <div className="flex justify-start">
              <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin text-amber-500" />
                <span className="text-xs text-slate-400">Procesez...</span>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 shrink-0 space-y-2">
        <div className="flex gap-1.5">
          <input type="text" value={textInput} onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleTextSend()}
            placeholder="Sau scrie aici..." disabled={processing}
            className="flex-1 text-xs rounded-lg border border-slate-200 px-3 py-2 focus:border-amber-400 outline-none"
            data-testid="voice-agent-text-input" />
          <Button size="sm" onClick={handleTextSend} disabled={processing || !textInput.trim()} className="bg-amber-500 hover:bg-amber-600 text-white h-8 w-8 p-0">
            <Send className="w-3 h-3" />
          </Button>
        </div>

        <div className="flex items-center justify-center gap-3">
          {!listening ? (
            <button onClick={startContinuousListening} disabled={processing}
              className="w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 hover:scale-105 transition-all shadow-lg"
              data-testid="voice-start-btn">
              <Mic className="w-6 h-6 text-white" />
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <button onClick={togglePause} className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${paused ? "bg-green-500" : "bg-yellow-500"}`} data-testid="voice-pause-btn">
                {paused ? <Play className="w-5 h-5 text-white" /> : <Pause className="w-5 h-5 text-white" />}
              </button>
              <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg ${paused ? "bg-slate-300" : speaking ? "bg-blue-500 animate-pulse" : "bg-gradient-to-br from-amber-400 to-orange-500 animate-pulse"}`}>
                {speaking ? <Volume2 className="w-6 h-6 text-white" /> : paused ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
              </div>
              <button onClick={stopListening} className="w-12 h-12 rounded-full flex items-center justify-center bg-red-500 hover:bg-red-600 shadow-lg" data-testid="voice-stop-btn">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
        </div>
        <p className="text-[10px] text-center text-slate-400">
          {!listening ? "Apasa microfonul pentru ascultare continua" : paused ? "Pauza — apasa Play" : speaking ? "Raspund vocal..." : "Ascult... vorbiti liber"}
        </p>
      </div>
      <audio ref={audioRef} className="hidden" />
    </div>
  );
}
